import React, { useEffect, useMemo, useRef, useState } from 'react';
import useFullScreen from '@op/hooks/useFullScreen';
import CardCommon from '@op/components/CardCommon';
import { Segment } from 'tea-component';
import { getPieces } from '@intelligence/pages/MarketProfile/_util';
import { transCountryText } from '@op/utils';
import { GeoJSON } from '@intelligence/types/common';
import * as echarts from 'echarts/core';
import { importJSON } from '@intelligence/utils/echartsUtils';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import useIntls from '@opm/hooks/useIntls';
import classnames from 'classnames';
import { countryCn, countryEn } from '@op/constants';

import BubbleCommon from '@op/components/BubbleCommon';
import { countryList } from '@opm/components/FilterCommon/constants/country';
import { hashtagCountryDistributionTop5DetailTipsMap, hashtagMapFullTipsMap } from '@opm/components/BubbleCommon';

export const selectOptions = [
  { text: 'Views', title: 'Views', value: 'tweets_views' },
  { text: 'Videos', title: 'Videos', value: 'tweets_posts' },
];

const mapCache = {
  worldMap: null as GeoJSON | null,
};

const HashtagMapChart = (props: { data: any[] }) => {
  const { data } = props;
  const [selected, setSelected] = useState('tweets_views');

  const { intl } = useIntls();
  const { ScreenModal, FullIcon } = useFullScreen();

  return (
    <div className="dbom-hashtag-map-chart-wrapper">
      <MapChart
        leftTitle={
          <Selector
            selected={selected}
            setSelected={setSelected}
            className="segment-light"
          />
        }
        rightTitle={
          <FullIcon placement="left" tipsMap={hashtagMapFullTipsMap} />
        }
        formatData={data}
        selected={selected}
      ></MapChart>
      <ScreenModal
        title={
          <BubbleCommon
            placement="top"
            tipsMap={hashtagCountryDistributionTop5DetailTipsMap(countryList)}
          >
            {intl({ id: 'Country Distribution' })}
          </BubbleCommon>
        }
        hasRightIcon={false}
      >
        <CardCommon
          contentCard
          title={<Selector selected={selected} setSelected={setSelected} />}
        >
          <MapChart
            formatData={data}
            selected={selected}
            height={500}
          ></MapChart>
        </CardCommon>
      </ScreenModal>
    </div>
  );
};

function Selector({
  className,
  selected,
  setSelected,
}: {
  className?: string;
  selected: string;
  setSelected: (value: string) => void;
}) {
  const { formatOptionsMessage } = useIntls();

  return (
    <Segment
      value={selected}
      className={classnames('dbom-hashtag-map-chart-segment', className)}
      onChange={(value) => setSelected(value)}
      options={formatOptionsMessage(selectOptions)}
    />
  );
}

const MapChart = (props: {
  leftTitle?: JSX.Element;
  rightTitle?: JSX.Element;
  formatData: any[];
  selected: string;
  height?: number;
}) => {
  const { leftTitle, rightTitle, formatData, selected, height = 200 } = props;
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstance = useRef<echarts.EChartsType | null>(null);
  const [map, setMap] = useState<GeoJSON>();
  const [mapLoading, setMapLoading] = useState<boolean>(true);
  const isMounted = React.useRef(true);
  const { intl, isEn } = useIntls();
  const [loading, setLoading] = useState(false);

  const data = useMemo(() => {
    return formatData?.map((val) => ({
      ...val,
      value: val?.[selected] || 0,
    }));
  }, [formatData, selected]);

  useEffect(() => {
    const getMapChart = async () => {
      try {
        // 检查是否已经有缓存
        if (mapCache.worldMap) {
          if (!map && isMounted.current) {
            console.debug('Using cached map data');
            setMap(mapCache.worldMap);
            setMapLoading(false);
          }
          return;
        }

        // 检查echarts是否已注册世界地图
        if (echarts.getMap('world')) {
          const existingMap = echarts.getMap('world').geoJson;

          if (existingMap && isMounted.current) {
            console.debug('Using existing echarts map');
            mapCache.worldMap = existingMap;
            setMap(existingMap);
            setMapLoading(false);
          }
        } else {
          const mapData = (await importJSON(
            'https://ogdb-cdn.intlgame.cn/front/static/world.json',
          )) as GeoJSON;

          if (mapData && isMounted.current) {
            mapCache.worldMap = mapData;
            echarts.registerMap('world', mapData);
            setMap(mapData);
            setMapLoading(false);
          }
        }
      } catch (e) {
        console.error('Failed to load map data:', e);
        setMapLoading(false);
      }
    };
    getMapChart();
    return () => {
      isMounted.current = false;
    };
  }, []);

  const countryNameCnMap: any = Object.keys(countryEn).reduce(
    (pre, key) => {
      const enKey = countryEn[key];
      return {
        ...pre,
        [enKey]: countryCn[key],
      };
    },
    { Russia: '俄罗斯' } satisfies Record<string, string>,
  );

  const options = useMemo(() => {
    const dealData =
      data?.map((item) => ({
        ...item,
        name: transCountryText(item.country),
      })) || [];

    return {
      tooltip: {
        trigger: 'item',
        padding: 0,
        borderWidth: 0,
        formatter: (params: any) => {
          const data = params?.data;
          if (!data) return '';

          const title = params.name || '';
          const selectedOption = selectOptions.find(
            (item) => item.value === selected,
          );
          const name = selectedOption?.title || '';
          return formatChartParamsDate({
            title,
            params,
            listTitleFunc: () => intl(name),
            textShowFun: (
              marker: string,
              seriesName: string,
              className?: string,
            ) => {
              let markerIcon: string = marker;
              if (marker.includes('background-color:transparent')) {
                markerIcon = markerIcon.replace(
                  'background-color:transparent',
                  'background-color: #5086F3',
                );
              }
              return `<span class="chart-common-formatter-marker ${className}">${markerIcon}</span>&nbsp;${seriesName}`;
            },
          });
        },
        label: { show: false },
      },
      visualMap: [
        {
          realtime: false,
          calculable: false,
          type: 'piecewise',
          min: 0,
          max: 10000000000,
          itemWidth: 14,
          itemHeight: 30,
          inverse: false,
          itemGap: 0,
          itemSymbol: 'rect',
          bottom: '1%',
          left: '1%',
          // textGap:10,
          textStyle: {
            lineHeight: 30,
            color: '#838697',
            fontSize: 14,
          },
          // 点击后的颜色
          outOfRange: {
            color: '#ffdf33',
            symbolSize: [30, 100],
          },
          pieces: getPieces(data),
        },
      ],
      series: [
        {
          type: 'map',
          mapType: 'world',
          roam: 'move',
          itemStyle: {
            normal: {
              borderColor: 'rgba(190, 190, 190,0.5)',
              borderWidth: 1,
              areaColor: '#f2f2f2',
            },
            emphasis: {
              // borderColor: '#000',
              // borderWidth: 1.5,
              label: { show: false },
              itemStyle: {
                borderColor: '#000',
                borderWidth: '2',
                borderType: 'solid',
                shadowColor: 'rgba(0, 0, 0, 0.5)',
                shadowBlur: 10,
              },
            },
          },
          data: dealData,
          nameMap: !isEn ? countryNameCnMap : null,
        },
      ],
    };
  }, [data, isEn, countryNameCnMap]);
  useEffect(() => {
    if (!chartRef.current || mapLoading || !map) return;

    if (!chartInstance.current) {
      chartInstance.current = echarts.init(chartRef.current);
    }

    const chart = chartInstance.current;
    chart.setOption(options);

    return () => {
      chart.dispose();
      chartInstance.current = null;
    };
  }, [options, map, mapLoading]);

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [selected]);

  return (
    <CardCommon
      contentCard
      title={leftTitle}
      dropdownsFilterBeforeElement={rightTitle}
      loading={loading}
      loadingHeight={height}
      empty={data?.length === 0}
      emptyHeight={height}
    >
      <div ref={chartRef} className="dbom-hashtag-map-chart" />
    </CardCommon>
  );
};

export default HashtagMapChart;
